import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Dialog } from '@headlessui/react';
import { Modal } from 'components/Modal';
import { GET_BILLING_INVOICE_BREAKDOWN } from 'gql/queries';

export type InvoiceBreakdownBill = {
  id: string;
  number?: string | number | null;
  date?: string | null;
  amount?: string | number | null;
  invoiceId?: string | null;
  invoicePdfId?: string | null;
};

type BillingSource = {
  id: string;
  sourceId: string;
  sourceType: string;
  displayReference?: string | null;
  amount: string | number;
  currencyCode: string;
  description: string;
  occurredAt?: string | null;
  billingProvider: string;
  rolledForward: boolean;
  originalBillDate?: string | null;
};

type SourceAggregate = {
  aggregate?: {
    count: number;
    sum?: { amount?: string | number | null } | null;
  } | null;
};

type BreakdownData = {
  sources: BillingSource[];
  lineSummary: SourceAggregate;
  statementSummary: SourceAggregate;
};

const PAGE_SIZE = 100;
const sourceTypeLabels: Record<string, string> = {
  PROTECTION_FEE: 'Delivery Guarantee',
  RETURN_FEE: 'Returns',
  ORDER_EDIT_FEE: 'Order Editing',
};
const sourceTypeLabel = (sourceType?: string | null) =>
  sourceType &&
  Object.prototype.hasOwnProperty.call(sourceTypeLabels, sourceType)
    ? sourceTypeLabels[sourceType]
    : 'Other fee';
const money = (amount: string | number | null | undefined) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(amount ?? 0),
  );

const savedPdfUrl = (value?: string | null) => {
  try {
    const url = new URL(value ?? '');
    return ['https:', 'http:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

export const BillingInvoiceBreakdown = ({
  bill,
  storeId,
  onClose,
}: {
  bill: InvoiceBreakdownBill;
  storeId: string;
  onClose: () => void;
}) => {
  const [offset, setOffset] = useState(0);
  useEffect(() => setOffset(0), [bill.id, bill.invoiceId, storeId]);
  const filters = {
    storeId: { _eq: storeId },
    targetBillId: { _eq: bill.id },
    _not: { billingExclusion: {} },
  };
  const lineFilters = bill.invoiceId
    ? {
        ...filters,
        invoiceId: { _eq: bill.invoiceId },
        billingProvider: { _eq: 'STRIPE_BILLING' },
      }
    : filters;
  const { data, loading, error, refetch } = useQuery<BreakdownData>(
    GET_BILLING_INVOICE_BREAKDOWN,
    {
      variables: { filters, lineFilters, limit: PAGE_SIZE, offset },
      skip: !storeId || !bill.id,
      notifyOnNetworkStatusChange: true,
    },
  );
  const count = Number(data?.lineSummary?.aggregate?.count ?? 0);
  const lineTotal = data?.lineSummary?.aggregate?.sum?.amount;
  const statementTotal = data?.statementSummary?.aggregate?.sum?.amount;
  const mismatch =
    statementTotal != null &&
    bill.amount != null &&
    Math.round(Number(statementTotal) * 100) !==
      Math.round(Number(bill.amount) * 100);
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const failed = error || !storeId || !bill.id || (!loading && !data);
  const pdfUrl = savedPdfUrl(bill.invoicePdfId);

  return (
    <Modal
      open
      openModal={(open) => !open && onClose()}
      containerClass="w-full max-w-5xl"
    >
      <Dialog.Title className="text-xl font-semibold text-gray-900">
        Invoice #{bill.number ?? 'N/A'} breakdown
      </Dialog.Title>
      <p className="mt-1 text-sm text-gray-600">
        {bill.date ? `Billing date: ${bill.date}. ` : ''}All amounts in USD.
      </p>
      {loading ? (
        <p role="status" className="py-8 text-sm text-gray-600">
          Loading invoice charges…
        </p>
      ) : failed ? (
        <div className="py-6">
          <p role="alert" className="text-sm text-red-700">
            Invoice charges could not be loaded.
          </p>
          <button
            type="button"
            className="mt-3 text-sm font-medium text-blue-700"
            onClick={() => refetch()}
          >
            Try again
          </button>
        </div>
      ) : !count ? (
        <div className="py-8 text-sm text-gray-600">
          <p>No stored line items are available for this invoice.</p>
          <p className="mt-2">
            Older invoices may not have itemized charges stored. Use the saved
            breakdown PDF when available.
          </p>
          {pdfUrl && (
            <a
              className="mt-3 inline-block font-medium text-blue-700"
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download breakdown PDF
            </a>
          )}
        </div>
      ) : (
        <>
          <div className="mt-5 flex flex-wrap gap-x-10 gap-y-3 rounded-md bg-gray-50 p-4">
            <div>
              <p className="text-sm text-gray-600">
                {bill.invoiceId
                  ? 'Stripe invoice charges'
                  : 'Billing statement charges'}
              </p>
              <p className="text-xl font-semibold text-gray-900">
                {money(lineTotal)}
              </p>
              <p className="text-sm text-gray-600">
                {count.toLocaleString('en-US')} charges
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Billing statement total</p>
              <p className="text-xl font-semibold text-gray-900">
                {money(bill.amount)}
              </p>
            </div>
          </div>
          {bill.invoiceId &&
            lineTotal != null &&
            statementTotal != null &&
            Number(lineTotal) !== Number(statementTotal) && (
              <p className="mt-3 text-sm text-gray-600">
                This view contains only charges assigned to this Stripe invoice.
                Shopify charges are billed separately and included in the billing
                statement total.
              </p>
            )}
          {mismatch && (
            <p
              role="alert"
              className="mt-3 rounded-md bg-yellow-50 p-3 text-sm text-yellow-900"
            >
              Stored charges total {money(statementTotal)}, while this billing
              statement shows {money(bill.amount)}. Some historical charge
              details may be unavailable.
            </p>
          )}
          <div className="mt-4 max-h-[55vh] overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  {[
                    'Reference',
                    'Type',
                    'Date',
                    'Description',
                    'Provider',
                    'Fee (USD)',
                  ].map((heading) => (
                    <th key={heading} scope="col" className="px-4 py-3">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white text-gray-700">
                {data?.sources.map((source) => (
                  <tr key={source.id}>
                    <td className="px-4 py-3 break-all">
                      {source.displayReference || source.sourceId}
                    </td>
                    <td className="px-4 py-3">
                      {sourceTypeLabel(source.sourceType)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {source.occurredAt?.slice(0, 10) || 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <span>{source.description}</span>
                      {source.rolledForward && (
                        <p className="mt-1 text-xs text-gray-500">
                          Carried forward
                          {source.originalBillDate
                            ? ` from bill dated ${source.originalBillDate}`
                            : ' from an earlier billing period'}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {source.billingProvider === 'STRIPE_BILLING'
                        ? 'Stripe'
                        : source.billingProvider === 'SHOPIFY_BILLING'
                        ? 'Shopify'
                        : 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {money(source.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 text-sm">
            <button
              type="button"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={!offset}
              className="rounded border border-gray-300 px-3 py-2 disabled:opacity-50"
            >
              Previous page
            </button>
            <span>
              Page {Math.floor(offset / PAGE_SIZE) + 1} of {pages}
            </span>
            <button
              type="button"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= count}
              className="rounded border border-gray-300 px-3 py-2 disabled:opacity-50"
            >
              Next page
            </button>
          </div>
        </>
      )}
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
        >
          Close breakdown
        </button>
      </div>
    </Modal>
  );
};
